import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Toolbar as MuiToolbar,
  Typography,
  useTheme,
  useMediaQuery,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Avatar,
  Box,
  Divider,
} from "@mui/material";
import {
  Menu as MenuIcon,
  People as PeopleIcon,
  Android as AndroidIcon,
  Group as GroupIcon,
  Logout as LogoutIcon,
  SwapHoriz as SwapHorizIcon,
  MeetingRoom as MeetingRoomIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  VpnKey as VpnKeyIcon,
  Person as PersonIcon,
} from "@mui/icons-material";
import { useAuth } from "@/hooks/useAuth";
import { usePermissions } from "@/hooks/usePermissions";
import { userService, authService } from "@/services/api";
import {
  StyledRoot,
  StyledAppBar,
  StyledDrawer,
  MainContent,
  Toolbar,
  ToolbarActions,
  UserInfo,
} from "./styles";

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(true);
  const { logout } = useAuth();
  const { hasPermission } = usePermissions();

  // 修改密碼對話框相關狀態
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    password: "",
    confirmPassword: "",
  });
  const [passwordError, setPasswordError] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  // 獲取當前用戶資訊
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const userData = await authService.getCurrentUser();
        setCurrentUser(userData);
      } catch (error) {
        console.error("無法獲取當前用戶資訊:", error);
      }
    };

    fetchCurrentUser();
  }, []);

  const menuItems = [
    {
      text: "使用者管理",
      icon: <PeopleIcon />,
      path: "/users",
      permission: "admin",
    },
    {
      text: "機器人管理",
      icon: <AndroidIcon />,
      path: "/bots",
      permission: "admin",
    },
    {
      text: "機器人用戶查詢",
      icon: <GroupIcon />,
      path: "/bot-users",
      permission: "admin", // 使用同樣的權限或自定義
    },
    {
      text: "機器人轉發管理",
      icon: <SwapHorizIcon />,
      path: "/bot-rules",
      permission: "admin", // 使用同樣的權限或自定義
    },
  ];

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleToggleDrawerVisibility = () => {
    setDrawerVisible(!drawerVisible);
  };

  const handleOpenPasswordDialog = () => {
    setPasswordDialogOpen(true);
    setPasswordForm({
      password: "",
      confirmPassword: "",
    });
    setPasswordError("");
    setPasswordSuccess("");
  };

  const handleClosePasswordDialog = () => {
    setPasswordDialogOpen(false);
  };

  const handleChangePassword = async () => {
    // 驗證密碼
    if (passwordForm.password.length < 6) {
      setPasswordError("密碼長度至少需要 6 個字符");
      return;
    }

    if (passwordForm.password !== passwordForm.confirmPassword) {
      setPasswordError("兩次輸入的密碼不一致");
      return;
    }

    try {
      if (currentUser) {
        await userService.updateUserPassword(
          currentUser.id,
          passwordForm.password
        );
        setPasswordSuccess("密碼已成功更新");
        setPasswordError("");

        // 3秒後自動關閉對話框
        setTimeout(() => {
          handleClosePasswordDialog();
        }, 2000);
      }
    } catch (error) {
      setPasswordError("更新密碼失敗: " + (error.message || "未知錯誤"));
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const drawer = (
    <>
      <MuiToolbar />
      <List>
        {menuItems.map(
          (item) =>
            hasPermission(item.permission) && (
              <ListItem
                button
                key={item.text}
                onClick={() => handleNavigation(item.path)}
                selected={location.pathname === item.path}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItem>
            )
        )}
      </List>
    </>
  );

  return (
    <StyledRoot>
      <StyledAppBar position="fixed">
        <MuiToolbar>
          <Toolbar>
            <div style={{ display: "flex", alignItems: "center" }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={handleDrawerToggle}
                sx={{ mr: 2, display: { sm: "none" } }}
              >
                <MenuIcon />
              </IconButton>
              {/* 使用 icon.png 替代 AdminIcon */}
              <img
                src="/icon.png"
                alt="Icon"
                style={{
                  width: 50,
                  height: 50,
                  marginRight: "12px",
                }}
              />
              <Typography variant="h6" noWrap component="div">
                BotRelayX System
              </Typography>
            </div>

            <ToolbarActions>
              {/* 顯示登入用戶名稱 */}
              {currentUser && (
                <UserInfo>
                  <PersonIcon sx={{ mr: 0.5 }} fontSize="small" />
                  <Typography variant="body2">
                    {currentUser.username || "使用者"}
                  </Typography>
                </UserInfo>
              )}
              {/* 修改密碼按鈕 */}
              <Tooltip title="修改密碼">
                <IconButton color="inherit" onClick={handleOpenPasswordDialog}>
                  <VpnKeyIcon />
                </IconButton>
              </Tooltip>

              {/* 顯示/隱藏側邊欄按鈕 */}
              {!isMobile && (
                <Tooltip title={drawerVisible ? "隱藏側邊欄" : "顯示側邊欄"}>
                  <IconButton
                    color="inherit"
                    onClick={handleToggleDrawerVisibility}
                  >
                    {drawerVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </Tooltip>
              )}

              {isMobile ? (
                <Tooltip title="登出">
                  <IconButton color="inherit" onClick={handleLogout}>
                    <LogoutIcon />
                  </IconButton>
                </Tooltip>
              ) : (
                <Button
                  color="inherit"
                  onClick={handleLogout}
                  startIcon={<LogoutIcon />}
                >
                  登出
                </Button>
              )}
            </ToolbarActions>
          </Toolbar>
        </MuiToolbar>
      </StyledAppBar>

      {/* Mobile Drawer */}
      {isMobile && (
        <StyledDrawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
        >
          {drawer}
        </StyledDrawer>
      )}

      {/* Desktop Drawer - 只在 drawerVisible 為 true 時顯示 */}
      {!isMobile && drawerVisible && (
        <StyledDrawer variant="permanent">{drawer}</StyledDrawer>
      )}

      {/* 依據抽屜可見性調整主內容區域 */}
      <MainContent
        sx={{
          marginLeft: !isMobile && drawerVisible ? undefined : 0,
          width: "100%",
        }}
      >
        {children}
      </MainContent>

      {/* 修改密碼對話框 */}
      <Dialog open={passwordDialogOpen} onClose={handleClosePasswordDialog}>
        <DialogTitle>修改密碼</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}

          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}

          <TextField
            autoFocus
            margin="dense"
            label="新密碼"
            type="password"
            fullWidth
            value={passwordForm.password}
            onChange={(e) =>
              setPasswordForm({ ...passwordForm, password: e.target.value })
            }
          />

          <TextField
            margin="dense"
            label="確認新密碼"
            type="password"
            fullWidth
            value={passwordForm.confirmPassword}
            onChange={(e) =>
              setPasswordForm({
                ...passwordForm,
                confirmPassword: e.target.value,
              })
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog}>取消</Button>
          <Button onClick={handleChangePassword} color="primary">
            確認修改
          </Button>
        </DialogActions>
      </Dialog>
    </StyledRoot>
  );
};

export default Layout;
